/**
 * utils/txnStatus.js
 * Pure derived-value math for a transaction (Phase T1 PRD §4.3). Total, sisa, status, and
 * due-state are NEVER persisted on the transaction — always recomputed from lines/payments/
 * returs so no stored field can drift out of sync (invariant 9). Whole-Rupiah integers
 * throughout.
 *
 * Cost items Brief 2: `total` now also includes "tagihan"/"tagihanSupplier"-type costItems —
 * real money the transaction's own counterparty owes/is owed, same as a goods line. "biaya"-
 * type items never touch it (that money goes to someone who isn't the counterparty). `txnSisa`/
 * `txnStatus`/`pctPaid` all derive from `txnTotal`, so they pick this up for free with no code
 * of their own. `goodsTotal` is `txnTotal`'s OLD, pre-Brief-2 definition, kept as its own real
 * export — `utils/pagination.js` (printed-document totals) deliberately depends on THIS
 * function, not `txnTotal`, so a printed total can never silently start including cost items
 * even if a future call site there were changed to pass a full `txn` object instead of a bare
 * `{lines}` one — see that file's own header comment.
 *
 * Corrective pass, Piece 2: `txnTotal` also subtracts "potongan"-type costItems (a discount
 * that lowers what the counterparty owes/is owed — the mirror image of tagihan/tagihanSupplier).
 * `totalDikurangiBiaya` needed NO code change — it already reads the live `txnTotal(txn)`, so
 * it inherits Potongan's effect transitively, same as it already inherited Tagihan's. A new
 * sibling, `totalDitambahBiaya`, is Pembelian's own equivalent (Pembelian's money only ever
 * flows OUT, so Biaya there is a second outflow to ADD, not net against).
 *
 * Piutang & Utang batch 3: `transferStatusFor` now lives here (moved from `utils/
 * piutangUtang.js`, which re-exports it unchanged for backward compatibility) — see its own
 * header comment below. `txnStatus` gained an optional `perspectiveBranchId` second argument so
 * a TRANSFER's PIUTANG/UTANG answer can be resolved per-side instead of the old hardcoded
 * "always PIUTANG" — see that function's own header comment for the full backward-compatibility
 * contract (every existing, perspective-less call site is byte-for-byte unaffected).
 */
import { sumCostItemsRaisingTotal, sumBiayaCostItems, sumPotonganCostItems } from "./costItems";

/** Σ qty*unitPrice across every line — no costItems knowledge at all. */
export function goodsTotal(txn) {
  return (txn.lines || []).reduce((sum, line) => sum + line.qty * line.unitPrice, 0);
}

/** goodsTotal + Σ tagihan/tagihanSupplier costItems − Σ potongan costItems (see the file
 *  header comment). */
export function txnTotal(txn) {
  return goodsTotal(txn) + sumCostItemsRaisingTotal(txn.costItems) - sumPotonganCostItems(txn.costItems);
}

/**
 * A transaction's Total Nilai must never go negative — validated here as its OWN explicit
 * rule, not left as an emergent side effect of a different check.
 *
 * Why this function exists at all: as of this writing, `utils/payments.js`'s own `sum(payments)
 * <= total` rule (invariant 16) already makes a negative total un-persistable, purely by
 * accident — a payment row can never be negative (fix #6, `validatePaymentSet`), so `sum` is
 * always >= 0, and the instant `total < 0`, `sum > total` is true no matter what the payments
 * array holds, including an empty one. That means `createTransaction`/`editTransaction` already
 * reject every negative-total case today, WITHOUT this function existing — confirmed by direct
 * reproduction, not assumed (see this feature's own investigation history). So this function
 * does not close a reachable data-corruption hole. It does two other things instead:
 *   1. Tells the user the truth. Without it, someone who drives total negative purely by editing
 *      lines/cost items — having never touched a payment row — is told "jumlah pembayaran
 *      melebihi total" (payment exceeds total), which is meaningless to them and points at the
 *      wrong field entirely.
 *   2. Makes the rule explicit rather than emergent. The protection above depends ENTIRELY on
 *      payments never being negative. `txn.returs` is a reserved placeholder for a future
 *      returns feature (see the root CLAUDE.md), and a return credit is very plausibly
 *      implemented as something that reduces what's owed — a negative payment in all but name.
 *      If that ships without this rule stated somewhere explicit, the negative-total hole
 *      reopens silently, since nothing in the code currently says "total >= 0" on its own terms.
 *
 * Zero is a valid total (a transaction that nets to exactly nothing owed is coherent — see
 * txnStatus's own LUNAS-at-zero behavior) — only strictly-below-zero is rejected.
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validateTxnTotal(total) {
  return total < 0
    ? {
        ok: false,
        error: "Total Nilai minus — Potongan/Tagihan melebihi Subtotal Barang. Kurangi Potongan/Tagihan atau tambah barang.",
      }
    : { ok: true, error: null };
}

/** txnTotal minus the biaya-only sum — tagihan/tagihanSupplier/potongan are already baked into
 *  txnTotal, so neither is subtracted again here. Never stored, computed wherever displayed.
 *  Penjualan/Transfer's own version — Pembelian uses totalDitambahBiaya instead (below). */
export function totalDikurangiBiaya(txn, expenseTotal = 0) {
  // Biaya Lain-Lain (2026-10-11): our own costs now live in `data.expenses`; the caller passes
  // the sum of this transaction's linked ones. Old "biaya" rows are moved there by the v26
  // migration, so the costItems sum is only ever 0 now — kept as a harmless safety net.
  return txnTotal(txn) - sumBiayaCostItems(txn.costItems) - expenseTotal;
}

/** txnTotal plus the biaya-only sum — Pembelian's own equivalent of totalDikurangiBiaya.
 *  Pembelian's money only ever flows OUT (to the supplier); biaya is a SECOND outflow (to
 *  someone else), so the useful number is the true total cost (addition), not a net take-home
 *  (subtraction) — there's no inflow to net against. Never stored, computed wherever displayed. */
export function totalDitambahBiaya(txn, expenseTotal = 0) {
  return txnTotal(txn) + sumBiayaCostItems(txn.costItems) + expenseTotal;
}

/**
 * Remaining unpaid balance: total - Σpayments - Σretur credit. Only a retur settled with
 * "REDUCE_SISA" reduces the balance — a "CASH_REFUND" retur is settled outside the invoice
 * (money handed back), so it never touches sisa.
 */
export function txnSisa(txn) {
  const total = txnTotal(txn);
  const paid = (txn.payments || []).reduce((sum, p) => sum + p.amount, 0);
  const returCredit = (txn.returs || [])
    .filter((r) => r.settlement === "REDUCE_SISA")
    .reduce((sum, r) => sum + r.value, 0);
  return total - paid - returCredit;
}

/**
 * PIUTANG or UTANG for ONE transfer, from ONE side's own books.
 *
 * **Moved here from `utils/piutangUtang.js` (Piutang & Utang batch 3)** — re-exported from
 * there unchanged (`export { transferStatusFor } from "./txnStatus"`) so every existing import
 * path keeps working. It belongs here, not there: this is single-transaction math (exactly this
 * file's own job — "Pure derived-value math for a transaction"), not the collection-bucketing
 * `piutangUtang.js` is scoped to. The move also closes what would otherwise have been a
 * circular import — `txnStatus()` below needs this function the instant it threads a
 * perspective through, and `piutangUtang.js` already imports `txnStatus`/`txnSisa` from THIS
 * file, so the old direction (this file importing back from `piutangUtang.js`) would have been
 * a genuine cycle.
 *
 * `perspectiveBranchId` identifies "whose books" — shaped exactly like the `branchId` convention
 * already used everywhere else in this codebase (`Salesperson.branchId`, `bankAccountFor`'s own
 * `branchId` param): `null` means Pusat's books; a real `data.branches` id means that specific
 * Cabang branch's books. **Deliberately NOT `txn.contactId`** — a TRANSFER's own `contactId`
 * always names the app-maintained internal stand-in for the OTHER (counterparty) side, never the
 * side whose books are being asked about (confirmed by direct read of `App.js`'s
 * `resolveTransferContact`: the PUSAT contact list holds one internal stand-in PER BRANCH
 * specifically because Pusat's books need a distinct counterparty per branch, while the CABANG
 * list holds exactly one, branch-independent, because every branch's own books see the identical
 * counterparty, Pusat). Reaching for `contactId` here would silently resolve the wrong side.
 *
 * The transfer's SOURCE side (`transferDirection` + `branchId` — a transfer only ever runs
 * Pusat <-> ONE Cabang branch, never branch-to-branch) is always PIUTANG from its OWN books; the
 * destination side is always UTANG from its OWN books. E.g. a Pusat -> MMC transfer
 * (`transferDirection: "PUSAT_TO_CABANG"`): from Pusat's own books (`perspectiveBranchId: null`)
 * it's PIUTANG (Pusat is the source); from MMC's own books (`perspectiveBranchId: "<mmc id>"`)
 * it's UTANG (MMC is the destination). Mirrored for `"CABANG_TO_PUSAT"`.
 *
 * Throws (an assertion against a caller precondition, same class as `outstanding`'s own TRANSFER
 * guard in `piutangUtang.js` — never guesses) when: `txn.type !== "TRANSFER"` (this question only
 * makes sense for a transfer); `perspectiveBranchId === undefined` (a caller asking this function
 * anything must already have picked a real side — `null` is the explicit "Pusat" answer,
 * `undefined` is never a silent alias for it, the same "presence, not truthiness" discipline this
 * app already applies elsewhere, e.g. `contactImportResolve.js`'s `readDecision`); or
 * `perspectiveBranchId` is a branch id that doesn't match `txn.branchId` (this transfer has
 * nothing to do with that branch at all — a scope error, not an unresolved direction).
 */
export function transferStatusFor(txn, perspectiveBranchId) {
  if (txn.type !== "TRANSFER") {
    throw new Error("transferStatusFor(): txn.type must be TRANSFER");
  }
  if (perspectiveBranchId === undefined) {
    throw new Error(
      "transferStatusFor(): perspectiveBranchId is required — pass null for Pusat's own books, " +
        "or a real branchId for that Cabang branch's own books."
    );
  }
  const perspectiveIsPusat = perspectiveBranchId === null;
  if (!perspectiveIsPusat && perspectiveBranchId !== txn.branchId) {
    throw new Error(
      "transferStatusFor(): perspectiveBranchId does not match this transfer's own branchId — " +
        "a transfer only ever runs Pusat <-> one Cabang branch, never branch-to-branch, so this " +
        "transfer has nothing to do with the branch asked about."
    );
  }
  const perspectiveIsSource = perspectiveIsPusat
    ? txn.transferDirection === "PUSAT_TO_CABANG"
    : txn.transferDirection === "CABANG_TO_PUSAT";
  return perspectiveIsSource ? "PIUTANG" : "UTANG";
}

/**
 * LUNAS when sisa is 0; otherwise PIUTANG (PENJUALAN/TRANSFER) or UTANG (PEMBELIAN) — the
 * whole-business view, unchanged, when `perspectiveBranchId` is omitted.
 *
 * **`perspectiveBranchId` (optional, Piutang & Utang batch 3)**: for a non-TRANSFER, always
 * ignored — an ordinary PENJUALAN/PEMBELIAN row's status never depends on perspective, so every
 * existing call site that never passes this argument (there are many — any caller that only
 * cares about the LUNAS/not-LUNAS boolean, e.g. `TxnNilaiCell`, `TransactionDetailModal`,
 * `BayarTagihanModal`'s eligibility check, `TransactionFormModal`'s `isLunas` seed,
 * `paymentSplit.shouldShowBucketBars`, `dueState` below) is byte-for-byte unaffected. For a
 * TRANSFER: omitted (`undefined`) keeps today's whole-business convention — "same business on
 * both ends," always PIUTANG when outstanding — so every one of those same untouched call sites
 * also keeps seeing exactly what they see today, including for a transfer row. Only when a real
 * perspective is supplied (`null` for Pusat, a real branchId for a Cabang branch) does this
 * delegate to `transferStatusFor` for the real, per-side PIUTANG/UTANG answer — closing the
 * documented Pembelian StatusPill inconsistency (root CLAUDE.md's own "what is deliberately not
 * built" entry) at its one real call site, `pages/Pembelian.js`'s own row render.
 */
export function txnStatus(txn, perspectiveBranchId) {
  if (txnSisa(txn) === 0) return "LUNAS";
  if (txn.type === "PEMBELIAN") return "UTANG";
  if (txn.type === "TRANSFER" && perspectiveBranchId !== undefined) {
    return transferStatusFor(txn, perspectiveBranchId);
  }
  return "PIUTANG";
}

/**
 * Percentage of `total` already paid off, 0-100, rounded. `total === 0` reads as fully paid
 * (100) rather than dividing by zero. The single shared formula behind every %-paid bar in the
 * app (Piutang & Utang's Outstanding cell, TxnNilaiCell's own bar, TransactionDetailModal's
 * Sisa Tagihan bar) — previously three independent copies of the same calculation.
 */
export function pctPaid(txn) {
  const total = txnTotal(txn);
  const sisa = txnSisa(txn);
  return total > 0 ? Math.min(100, Math.round(((total - sisa) / total) * 100)) : 100;
}

/** "YYYY-MM-DD" day-count difference, b - a in days (calendar dates, no time-of-day). */
function daysBetween(dateStrA, dateStrB) {
  const a = new Date(`${dateStrA}T00:00:00Z`);
  const b = new Date(`${dateStrB}T00:00:00Z`);
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

/**
 * Due state relative to `today` (passed in — never Date.now() here). LUNAS or no dueDate
 * both mean there's nothing to be due, so both resolve to 'none'.
 * @returns {{ kind: 'overdue'|'today'|'upcoming'|'none', days: number }}
 */
export function dueState(txn, today) {
  if (!txn.dueDate || txnStatus(txn) === "LUNAS") {
    return { kind: "none", days: 0 };
  }
  const diff = daysBetween(txn.dueDate, today); // positive = due date is in the future
  if (diff < 0) return { kind: "overdue", days: -diff };
  if (diff === 0) return { kind: "today", days: 0 };
  return { kind: "upcoming", days: diff };
}

/**
 * Shared "due within N days" horizon filter — extracted from `KontakPage.js`'s own Riwayat
 * Transaksi due-within filter (batch 2c-due) so the Bisnis page work (batch 3) can reuse the
 * IDENTICAL bucket decision instead of a second, copy-pasted one. This is the one place a
 * transaction's horizon bucket is decided; every screen offering a due-within/horizon filter
 * should read `dueState`/`dueWithinMatches` through here, never re-derive the "does N days from
 * today cover this transaction" question on its own.
 *
 * Deliberately the mirror image of a transaction-DATE filter (which asks "when did this
 * HAPPEN," backward-looking, can reach into the past): this asks "when is it DUE"
 * (`dueState(t, today)`, off `t.dueDate`), forward-looking ONLY — "N Hari Lagi," never "N Hari
 * Lalu." "Semua" (no filtering) is represented by the CALLER simply not invoking
 * `dueWithinMatches` at all (see `dueWithinActiveN`'s own `null` case below), never a special
 * case inside these functions. Deliberately EXCLUDES 'overdue' — overdue is a current-problem
 * state meant to be surfaced unconditionally (an always-visible banner/badge), never folded into
 * this forward-only "will become due soon" window.
 */
export const TXN_DUE_PRESETS = [
  ["7", "Jatuh Tempo 7 Hari Lagi"],
  ["30", "Jatuh Tempo 30 Hari Lagi"],
];
export const TXN_DUE_PRESET_LABEL = Object.fromEntries(TXN_DUE_PRESETS);

/** Does `due` (a `dueState()` result) fall inside the "N hari lagi" window? `n` is a plain
 *  resolved day-count (never a preset string/"semua"/"kustom" — the caller resolves those to
 *  either a real `n` or "don't call this at all" first, see `dueWithinActiveN` below). Inclusive
 *  of today (`kind === "today"`, including `n === 0` — "hari ini saja") and every day up to and
 *  including `n`; excludes 'overdue' and 'none' outright — both are handled elsewhere (an
 *  always-visible overdue disclosure, a "—" fallback), never folded into this forward-only
 *  window. */
export function dueWithinMatches(due, n) {
  if (due.kind === "today") return true;
  if (due.kind === "upcoming") return due.days <= n;
  return false; // 'overdue' | 'none'
}

/** Parses a raw "Kustom" text field into a real, applicable day-count, or `null` when nothing
 *  valid is currently typed — blank, non-numeric, a decimal, or negative all resolve to `null`
 *  ("not applied yet," never a guess — negative has no meaning under a forward-only window).
 *  `0` is a real, valid, meaningful value ("today only"), not filtered out here. No upper
 *  bound — a very large day-count is just a wide window, allowed exactly as typed. */
export function parseDueCustomDays(text) {
  if (text.trim() === "") return null;
  const n = Number(text);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : null;
}

/** The due-within filter's own resolved day-count — the ONE place a "semua"/a fixed preset
 *  (`"7"`/`"30"`)/`"kustom"` selection collapses to either a real `n` (apply the filter) or
 *  `null` (apply NO due-based filtering at all — "semua," or "kustom" with nothing valid typed
 *  yet). Every other reader (the filter chain, a "Menampilkan:" summary, an empty-state message)
 *  should ask this ONE question instead of re-deriving the preset/custom-text logic itself.
 *  `customText` is read only while `selection === "kustom"`; ignored (and safe to omit) for
 *  every other selection. */
export function dueWithinActiveN(selection, customText) {
  if (selection === "7") return 7;
  if (selection === "30") return 30;
  if (selection === "kustom") return parseDueCustomDays(customText);
  return null; // "semua"
}

/** The label naming the CURRENT due-within selection, or `null` when nothing is actually
 *  applied ("semua," or "kustom" with nothing valid typed yet — nothing to name). `activeN`
 *  must already be `dueWithinActiveN(selection, customText)` — this never re-derives it, so the
 *  two can never disagree. `0` reads as "Jatuh Tempo Hari Ini Saja," never "0 Hari Lagi" — the
 *  literal string "0 Hari Lagi" would misstate what an N=0 window actually does (there is no day
 *  AFTER today in it). */
export function dueWithinSummaryLabel(selection, activeN) {
  if (selection === "7" || selection === "30") return TXN_DUE_PRESET_LABEL[selection];
  if (selection === "kustom" && activeN != null) {
    return activeN === 0 ? "Jatuh Tempo Hari Ini Saja" : `Jatuh Tempo ${activeN} Hari Lagi`;
  }
  return null; // "semua", or "kustom" with nothing valid typed yet
}
